<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between">
      <h2 class="text-text-neutral-tertiary text-sm font-medium">
        {{ $t('products.packages.title') }}
      </h2>
      <button
        type="button"
        class="text-text-positive-primary text-sm font-semibold"
        @click="$emit('onUnselect')"
      >
        {{ t('products.packages.reset') }}
      </button>
    </div>
    <div
      ref="scrollContainer"
      class="no-scrollbar flex space-x-4 overflow-auto"
    >
      <ProductsPackage
        v-for="productPackage in packages"
        :key="productPackage.id"
        :checked="packageInfo.id === productPackage.id"
        :product-package="productPackage"
        :data-checked="packageInfo.id === productPackage.id"
        :is-single="packages?.length === 1"
        name="product-package"
        @onSelect="
          packageInfo.id === productPackage.id
            ? $emit('onUnselect')
            : $emit('onSelect', $event)
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue';
  import type { Airflow } from '@aviata/platform-types';
  import type { PackageInfo } from '@aviata/products-fns';
  import { useI18n } from 'vue-i18n';

  import ProductsPackage from './ProductsPackage.vue';

  const { t } = useI18n();
  const props = defineProps<{
    packages: Airflow.Products.Package[];
    packageInfo: PackageInfo;
    /**
     * Skip the auto-scroll-to-checked-package behavior — both the initial
     * on-mount one and the watch that re-centers when `packageInfo.id`
     * changes. QuickBookingPage preselects a package on open and re-runs
     * the preselect after passenger-save and fare-change; without this
     * opt-out, each preselect drags the whole page down to the extras
     * section.
     */
    disableAutoScroll?: boolean;
  }>();

  defineEmits<{
    onSelect: [selectedPackage: Airflow.Products.Package];
    onUnselect: [];
  }>();
  const scrollContainer = ref<HTMLElement | null>(null);

  function scrollCheckedPackageIntoView() {
    nextTick(() => {
      const container = scrollContainer.value;
      if (!container) return;

      const checkedPackage = container.querySelector(
        '[data-checked="true"]',
      ) as HTMLElement | null;
      if (checkedPackage) {
        checkedPackage.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    });
  }

  onMounted(() => {
    if (props.disableAutoScroll) {
      return;
    }
    scrollCheckedPackageIntoView();
  });

  watch(
    () => props.packageInfo.id,
    () => {
      if (props.disableAutoScroll) {
        return;
      }
      scrollCheckedPackageIntoView();
    },
  );
</script>
